/**
 * LoginForm.tsx — Custom Cognito login form (D-06) + self-service password reset (Phase 2.3)
 *
 * Authenticates via USER_SRP_AUTH (passwords never leave the browser in plaintext).
 *
 * Views (single card, step-machine):
 *  - signIn        — email + password; "Forgot password?" affordance
 *  - NEW_PASSWORD  — AdminCreateUser force-change challenge (D-09), overlays signIn
 *  - resetRequest  — enter email → Cognito emails a reset code
 *  - resetConfirm  — enter code + new password → sign in with the new password
 *
 * Forgot-password is enumeration-safe: requestPasswordReset resolves identically for a
 * missing account, and the copy is generic ("If an account exists…"). Password rules and
 * Cognito error copy come from the shared passwordPolicy module so every flow agrees.
 *
 * Migrated to shadcn Card/Input/Label/Button primitives (Phase 01.1 plan 03, D-02).
 */
import { useState } from 'react'
import type { FormEvent, ChangeEvent } from 'react'
import { useAuth } from './useAuth'
import { requestPasswordReset, confirmPasswordReset, signUp, confirmSignUp } from './authService'
import { validatePassword, formatAuthError } from './passwordPolicy'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { Logo } from '@/components/Logo'

type View = 'signIn' | 'resetRequest' | 'resetConfirm' | 'signUp' | 'signUpConfirm'

export interface LoginFormProps {
  /**
   * T-500: true when the app was opened via a share deep-link while signed out. Adds a one-line
   * context notice ("a location was shared with you — sign in to view it") so the recipient of a
   * shared link understands why they hit the wall. Context only — the login/invite flow is unchanged.
   */
  sharedLink?: boolean
}

export function LoginForm({ sharedLink = false }: LoginFormProps = {}) {
  const { signIn, completeNewPassword } = useAuth()

  const [view, setView] = useState<View>('signIn')

  // Sign-in
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // Force-change (AdminCreateUser D-09)
  const [needsNewPassword, setNeedsNewPassword] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Signup flow (SS1)
  const [signUpEmail, setSignUpEmail] = useState('')
  const [signUpPassword, setSignUpPassword] = useState('')
  const [signUpConfirmPw, setSignUpConfirmPw] = useState('')
  const [signUpCode, setSignUpCode] = useState('')
  const [signUpOrg, setSignUpOrg] = useState('')
  const [signUpRole, setSignUpRole] = useState('')

  // Reset flow
  const [resetEmail, setResetEmail] = useState('')
  const [resetCode, setResetCode] = useState('')
  const [resetNewPassword, setResetNewPassword] = useState('')
  const [resetConfirmPassword, setResetConfirmPassword] = useState('')

  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  function resetMessages() {
    setError(null)
    setInfo(null)
  }

  // ── Step: sign in ──────────────────────────────────────────────────────
  async function handleSignIn(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    resetMessages()
    setLoading(true)
    try {
      await signIn(email, password)
    } catch (err) {
      if (err instanceof Error && err.message === 'NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true)
      } else {
        setError(formatAuthError(err, 'signIn'))
      }
    } finally {
      setLoading(false)
    }
  }

  // ── Step: complete new-password challenge (first login) ────────────────
  async function handleNewPassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    resetMessages()
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    const check = validatePassword(newPassword)
    if (!check.valid) {
      setError(check.message ?? 'Password does not meet the requirements.')
      return
    }
    setLoading(true)
    try {
      await completeNewPassword(newPassword)
    } catch (err) {
      // finding d: if the challenge session has expired, the new-password step can never
      // succeed — drop back to sign-in so the user isn't stranded. Other errors (e.g. a
      // weak password) stay on the form so they can correct and retry.
      const isExpiredChallenge =
        err instanceof Error && /expired|invalid session/i.test(`${err.name} ${err.message}`)
      if (isExpiredChallenge) {
        setNeedsNewPassword(false)
        setNewPassword('')
        setConfirmPassword('')
        setView('signIn')
        setError('Your session expired. Please sign in again to continue.')
      } else {
        setError(formatAuthError(err, 'signIn'))
      }
    } finally {
      setLoading(false)
    }
  }

  // ── Step: request a reset code ─────────────────────────────────────────
  async function handleResetRequest(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    resetMessages()
    setLoading(true)
    try {
      await requestPasswordReset(resetEmail)
      // Enumeration-safe: resolves identically whether or not the account exists.
      setInfo("If an account exists for that email, we've sent a verification code.")
      setView('resetConfirm')
    } catch (err) {
      setError(formatAuthError(err, 'reset'))
    } finally {
      setLoading(false)
    }
  }

  // ── Step: confirm reset with code + new password ───────────────────────
  async function handleResetConfirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    resetMessages() // finding f: also clear any stale info banner, not just the error
    if (resetNewPassword !== resetConfirmPassword) {
      setError('New passwords do not match.')
      return
    }
    const check = validatePassword(resetNewPassword)
    if (!check.valid) {
      setError(check.message ?? 'Password does not meet the requirements.')
      return
    }
    setLoading(true)
    try {
      await confirmPasswordReset(resetEmail, resetCode, resetNewPassword)
      // Back to sign-in, prefilled, with a confirmation.
      setEmail(resetEmail)
      setPassword('')
      setResetCode('')
      setResetNewPassword('')
      setResetConfirmPassword('')
      setView('signIn')
      setInfo('Your password has been reset. Please sign in.')
    } catch (err) {
      setError(formatAuthError(err, 'reset'))
    } finally {
      setLoading(false)
    }
  }

  // ── Step: self-service signup ───────────────────────────────────────────
  async function handleSignUp(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    resetMessages()
    if (signUpPassword !== signUpConfirmPw) {
      setError('Passwords do not match.')
      return
    }
    const check = validatePassword(signUpPassword)
    if (!check.valid) {
      setError(check.message ?? 'Password does not meet the requirements.')
      return
    }
    setLoading(true)
    try {
      await signUp(signUpEmail, signUpPassword, signUpOrg, signUpRole)
      setInfo('Check your email for a verification code.')
      setView('signUpConfirm')
    } catch (err) {
      setError(formatAuthError(err, 'signIn'))
    } finally {
      setLoading(false)
    }
  }

  // ── Step: confirm signup with verification code ───────────────────────
  async function handleSignUpConfirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    resetMessages()
    setLoading(true)
    try {
      await confirmSignUp(signUpEmail, signUpCode)
      setEmail(signUpEmail)
      setPassword('')
      setSignUpCode('')
      setSignUpPassword('')
      setSignUpConfirmPw('')
      setView('signIn')
      setInfo('Account created! Please sign in.')
    } catch (err) {
      setError(formatAuthError(err, 'signIn'))
    } finally {
      setLoading(false)
    }
  }

  function startSignUp() {
    resetMessages()
    setSignUpEmail('')
    setSignUpPassword('')
    setSignUpConfirmPw('')
    setView('signUp')
  }

  function startReset() {
    resetMessages()
    setResetEmail(email)
    setResetCode('')
    setResetNewPassword('')
    setResetConfirmPassword('')
    setView('resetRequest')
  }

  function backToSignIn() {
    resetMessages()
    setView('signIn')
  }

  // finding d: escape hatch out of the first-login NEW_PASSWORD challenge.
  function backToSignInFromChallenge() {
    setNeedsNewPassword(false)
    setNewPassword('')
    setConfirmPassword('')
    resetMessages()
    setView('signIn')
  }

  return (
    <div
      data-testid="login-form"
      className="fixed inset-0 flex items-center justify-center bg-background font-sans"
    >
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          {/* Brand mark — centered in the login card. */}
          <Logo className="h-9 w-auto object-contain" />
          <p className="text-sm text-muted-foreground">EV Charging Site Intelligence</p>
        </CardHeader>
        <CardContent>
          {sharedLink && (
            <div
              data-testid="shared-link-notice"
              role="status"
              className="mb-4 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"
            >
              A location was shared with you. Sign in to view it.
            </div>
          )}
          {error && (
            <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          {info && (
            <div
              role="status"
              className="mb-4 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"
            >
              {info}
            </div>
          )}

          {view === 'signUp' ? (
            /* Self-service signup */
            <form onSubmit={(e) => void handleSignUp(e)} noValidate>
              <p className="mb-4 text-sm text-muted-foreground">
                Create an account to explore OptiYield.
              </p>
              <div className="mb-4">
                <Label htmlFor="signup-email">Email</Label>
                <Input
                  id="signup-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={signUpEmail}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSignUpEmail(e.target.value)}
                  required
                />
              </div>
              <div className="mb-4">
                <Label htmlFor="signup-org">
                  Company / Organization{' '}
                  <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  id="signup-org"
                  type="text"
                  autoComplete="organization"
                  placeholder="e.g. Acme Charging"
                  value={signUpOrg}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSignUpOrg(e.target.value)}
                />
              </div>
              <div className="mb-4">
                <Label htmlFor="signup-role">
                  How will you use OptiScore?{' '}
                  <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <select
                  id="signup-role"
                  value={signUpRole}
                  onChange={(e) => setSignUpRole(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="">Select one…</option>
                  <option value="Operator">Charger operator</option>
                  <option value="Installer">Installer / EPC</option>
                  <option value="Investor">Investor</option>
                  <option value="Site Host">Site host / Property owner</option>
                  <option value="Utility">Utility</option>
                  <option value="Municipality">City / State / DOT</option>
                  <option value="Fleet">Fleet manager</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="mb-4">
                <Label htmlFor="signup-password">Password</Label>
                <Input
                  id="signup-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="8+ chars, upper, lower, number"
                  value={signUpPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSignUpPassword(e.target.value)}
                  required
                />
              </div>
              <div className="mb-4">
                <Label htmlFor="signup-confirm">Confirm Password</Label>
                <Input
                  id="signup-confirm"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Re-enter your password"
                  value={signUpConfirmPw}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setSignUpConfirmPw(e.target.value)
                  }
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size={16} className="mr-2" />
                    Creating account…
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
              <button
                type="button"
                className="mt-4 w-full text-center text-xs text-muted-foreground underline-offset-2 hover:underline"
                onClick={backToSignIn}
              >
                Already have an account? Sign in
              </button>
            </form>
          ) : view === 'signUpConfirm' ? (
            /* Signup verification code */
            <form onSubmit={(e) => void handleSignUpConfirm(e)} noValidate>
              <p className="mb-4 text-sm text-muted-foreground">
                We sent a verification code to <strong>{signUpEmail}</strong>. Enter it below to
                activate your account.
              </p>
              <div className="mb-4">
                <Label htmlFor="signup-code">Verification code</Label>
                <Input
                  id="signup-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="6-digit code from your email"
                  value={signUpCode}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSignUpCode(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size={16} className="mr-2" />
                    Verifying…
                  </>
                ) : (
                  'Verify & Activate'
                )}
              </Button>
              <button
                type="button"
                className="mt-4 w-full text-center text-xs text-muted-foreground underline-offset-2 hover:underline"
                onClick={backToSignIn}
              >
                Back to sign in
              </button>
            </form>
          ) : needsNewPassword ? (
            /* First login: set a permanent password */
            <form onSubmit={(e) => void handleNewPassword(e)} noValidate>
              <div className="mb-4">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="8+ chars, upper, lower, number"
                  value={newPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="mb-4">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Re-enter your new password"
                  value={confirmPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setConfirmPassword(e.target.value)
                  }
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size={16} className="mr-2" />
                    Setting password…
                  </>
                ) : (
                  'Set Password & Sign In'
                )}
              </Button>
              <button
                type="button"
                className="mt-4 w-full text-center text-xs text-muted-foreground underline-offset-2 hover:underline"
                onClick={backToSignInFromChallenge}
              >
                Back to sign in
              </button>
              <p className="mt-3 text-center text-xs text-muted-foreground">
                Your administrator set a temporary password. Please choose a permanent one.
              </p>
            </form>
          ) : view === 'resetRequest' ? (
            /* Forgot password — step 1: request code */
            <form onSubmit={(e) => void handleResetRequest(e)} noValidate>
              <p className="mb-4 text-sm text-muted-foreground">
                Enter your email and we'll send a verification code to reset your password.
              </p>
              <div className="mb-4">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={resetEmail}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setResetEmail(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size={16} className="mr-2" />
                    Sending…
                  </>
                ) : (
                  'Send reset code'
                )}
              </Button>
              <button
                type="button"
                className="mt-4 w-full text-center text-xs text-muted-foreground underline-offset-2 hover:underline"
                onClick={backToSignIn}
              >
                Back to sign in
              </button>
            </form>
          ) : view === 'resetConfirm' ? (
            /* Forgot password — step 2: confirm code + new password */
            <form onSubmit={(e) => void handleResetConfirm(e)} noValidate>
              <div className="mb-4">
                <Label htmlFor="reset-code">Verification code</Label>
                <Input
                  id="reset-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="Code from your email"
                  value={resetCode}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setResetCode(e.target.value)}
                  required
                />
              </div>
              <div className="mb-4">
                <Label htmlFor="reset-new-password">New password</Label>
                <Input
                  id="reset-new-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="8+ chars, upper, lower, number"
                  value={resetNewPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setResetNewPassword(e.target.value)
                  }
                  required
                />
              </div>
              <div className="mb-4">
                <Label htmlFor="reset-confirm-password">Confirm password</Label>
                <Input
                  id="reset-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={resetConfirmPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setResetConfirmPassword(e.target.value)
                  }
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size={16} className="mr-2" />
                    Setting new password…
                  </>
                ) : (
                  'Set new password'
                )}
              </Button>
              <button
                type="button"
                className="mt-4 w-full text-center text-xs text-muted-foreground underline-offset-2 hover:underline"
                onClick={backToSignIn}
              >
                Back to sign in
              </button>
            </form>
          ) : (
            /* Sign in */
            <form onSubmit={(e) => void handleSignIn(e)} noValidate>
              <div className="mb-4">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="mb-4">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Your password"
                  value={password}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size={16} className="mr-2" />
                    Signing in…
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
              <button
                type="button"
                className="mt-4 w-full text-center text-xs text-muted-foreground underline-offset-2 hover:underline"
                onClick={startReset}
              >
                Forgot password?
              </button>
              <button
                type="button"
                className="mt-2 w-full text-center text-xs text-primary underline-offset-2 hover:underline"
                onClick={startSignUp}
              >
                Create an account
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
